import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cuentaUsuariosService, { PERMISO_CATALOGO } from "../../../_services/cart/cuentaUsuarios.service";
import alertService from "../../../_services/cart/alert.service";
import { ConfirmarAccion } from "./ConfirmarAccion";

/**
 * "Roles de la cuenta": la lista de roles y el editor con los permisos agrupados por modulo.
 * Portado de tienda-admin (`mi-estudio-usuarios/roles-manager`, 13b5a5b), con los mismos textos y
 * reglas: el rol Administrador es del sistema, no se edita ni se borra, y un rol con usuarios no se
 * puede eliminar.
 *
 * @param onCambio para que la pantalla de usuarios refresque los roles de sus selectores.
 * @param version  cambia cuando la pantalla de usuarios asigna o quita roles, para recontar.
 */
export const RolesCuenta = ({ onCambio, version }) => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Editor (crear / editar)
    const [showEditor, setShowEditor] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editNombre, setEditNombre] = useState("");
    const [editPermisos, setEditPermisos] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [editError, setEditError] = useState("");

    const [aEliminar, setAEliminar] = useState(null);

    const cargar = useCallback(() => {
        setIsLoading(true);
        cuentaUsuariosService
            .roles()
            .then((r) => setRoles(r.data || []))
            .catch(() => setRoles([]))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        cargar();
    }, [cargar, version]);

    const nuevoRol = () => {
        setEditId(null);
        setEditNombre("");
        setEditPermisos(new Set());
        setEditError("");
        setShowEditor(true);
    };

    const editarRol = (r) => {
        if (r.esAdmin) return;
        setEditId(r.id);
        setEditNombre(r.nombre);
        setEditPermisos(new Set(r.permisos));
        setEditError("");
        setShowEditor(true);
    };

    const cerrarEditor = () => {
        setShowEditor(false);
        setSaving(false);
    };

    const togglePermiso = (key) => {
        const s = new Set(editPermisos);
        if (s.has(key)) s.delete(key);
        else s.add(key);
        setEditPermisos(s);
    };

    const grupoCompleto = (g) => g.permisos.every((p) => editPermisos.has(p.key));

    const toggleGrupo = (g) => {
        const todos = grupoCompleto(g);
        const s = new Set(editPermisos);
        g.permisos.forEach((p) => (todos ? s.delete(p.key) : s.add(p.key)));
        setEditPermisos(s);
    };

    const guardar = () => {
        const nombre = editNombre.trim();
        if (!nombre) {
            setEditError("El nombre es obligatorio");
            return;
        }
        setSaving(true);
        const permisos = Array.from(editPermisos);
        const req =
            editId != null
                ? cuentaUsuariosService.actualizarRol(editId, nombre, permisos)
                : cuentaUsuariosService.crearRol(nombre, permisos);
        req.then(() => {
            setSaving(false);
            setShowEditor(false);
            alertService.msg("Rol guardado", "success");
            cargar();
            onCambio && onCambio();
        }).catch((e) => {
            setSaving(false);
            setEditError(e?.message || "No se pudo guardar el rol");
        });
    };

    const confirmarEliminar = () => {
        const r = aEliminar;
        setAEliminar(null);
        cuentaUsuariosService
            .eliminarRol(r.id)
            .then(() => {
                alertService.msg("Rol eliminado", "success");
                cargar();
                onCambio && onCambio();
            })
            .catch((e) => alertService.msg(e?.message || "No se pudo eliminar el rol", "error"));
    };

    return (
        <>
            <section className="cuenta-usuarios__card cuenta-usuarios__roles">
                <div className="cuenta-usuarios__roles-head">
                    <div>
                        <p className="cuenta-usuarios__eyebrow">PERMISOS</p>
                        <h3>Roles de la cuenta</h3>
                        <p className="cuenta-usuarios__texto">
                            Creá roles y asigná los permisos que cada uno tendrá. Después asignalos a tus usuarios.
                        </p>
                    </div>
                    <button type="button" className="button" data-type="accent" onClick={nuevoRol}>
                        <FontAwesomeIcon icon="fa-solid fa-plus" />
                        Nuevo rol
                    </button>
                </div>

                {isLoading && <p className="panel__estado-carga">Cargando…</p>}

                {!isLoading && (
                    <div className="cuenta-usuarios__roles-lista">
                        {roles.map((r) => (
                            <div key={r.id} className="cuenta-usuarios__rol-fila">
                                <div>
                                    <p className="cuenta-usuarios__nombre">
                                        <span>{r.nombre}</span>
                                        {r.esAdmin && <span className="cuenta-usuarios__sistema">Sistema</span>}
                                    </p>
                                    <p className="cuenta-usuarios__username">
                                        {r.esAdmin ? "Todos los permisos" : `${r.permisos.length} permisos`} · {r.usuarios}{" "}
                                        {r.usuarios === 1 ? "usuario" : "usuarios"}
                                    </p>
                                </div>
                                <div className="cuenta-usuarios__acciones">
                                    {r.esAdmin ? (
                                        <em className="cuenta-usuarios__sin-acciones">No editable</em>
                                    ) : (
                                        <>
                                            <button type="button" className="cuenta-usuarios__icono" title="Editar rol" onClick={() => editarRol(r)}>
                                                <FontAwesomeIcon icon="fa-solid fa-pen" />
                                            </button>
                                            <button
                                                type="button"
                                                className="cuenta-usuarios__icono cuenta-usuarios__icono--peligro"
                                                title="Eliminar rol"
                                                disabled={r.usuarios > 0}
                                                onClick={() => setAEliminar(r)}
                                            >
                                                <FontAwesomeIcon icon="fa-solid fa-trash" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Editor de rol */}
            {showEditor && (
                <div className="cuenta-usuarios__overlay" onClick={cerrarEditor}>
                    <div className="cuenta-usuarios__modal cuenta-usuarios__modal--grande" onClick={(e) => e.stopPropagation()}>
                        <div className="cuenta-usuarios__modal-head">
                            <div>
                                <p className="cuenta-usuarios__eyebrow">PERMISOS</p>
                                <h3>{editId != null ? "Editar rol" : "Nuevo rol"}</h3>
                            </div>
                            <button type="button" className="cuenta-usuarios__cerrar" onClick={cerrarEditor}>
                                <FontAwesomeIcon icon="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <div className="cuenta-usuarios__modal-body cuenta-usuarios__modal-scroll">
                            <div>
                                <label className="cuenta-usuarios__label">Nombre del rol</label>
                                <input
                                    type="text"
                                    placeholder="Ej: Vendedor, Depósito, Atención al cliente…"
                                    value={editNombre}
                                    onChange={(e) => setEditNombre(e.target.value)}
                                />
                            </div>

                            {PERMISO_CATALOGO.map((g) => (
                                <div key={g.grupo} className="cuenta-usuarios__grupo">
                                    <div className="cuenta-usuarios__grupo-head">
                                        <p>{g.grupo}</p>
                                        <button type="button" onClick={() => toggleGrupo(g)}>
                                            {grupoCompleto(g) ? "Desmarcar todo" : "Marcar todo"}
                                        </button>
                                    </div>
                                    <div className="cuenta-usuarios__grupo-permisos">
                                        {g.permisos.map((p) => (
                                            <label key={p.key}>
                                                <input
                                                    type="checkbox"
                                                    checked={editPermisos.has(p.key)}
                                                    onChange={() => togglePermiso(p.key)}
                                                />
                                                {p.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {editError && <div className="cuenta-usuarios__error">{editError}</div>}
                        </div>

                        <div className="cuenta-usuarios__modal-foot">
                            <button type="button" className="button" data-type="secondary" onClick={cerrarEditor}>
                                Cancelar
                            </button>
                            <button type="button" className="button" data-type="accent" disabled={saving} onClick={guardar}>
                                {saving && <FontAwesomeIcon icon="fa-solid fa-spinner" spin />}
                                {saving ? "Guardando..." : "Guardar rol"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {aEliminar && (
                <ConfirmarAccion
                    mensaje={`¿Eliminar el rol "${aEliminar.nombre}"?`}
                    confirmLabel="Eliminar"
                    onConfirmar={confirmarEliminar}
                    onCancelar={() => setAEliminar(null)}
                />
            )}
        </>
    );
};
